import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '../lib/auth';
import { captureEvent } from '../lib/analytics';
import GoogleAuthButton from '../components/GoogleAuthButton';
import './AuthPage.css';

const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(
    searchParams.get('error') === 'oauth'
      ? 'Google sign-up was cancelled or did not complete. Please try again.'
      : ''
  );
  const [loading, setLoading] = useState(false);

  // Read return URL saved by FitResultsPage (keep in sessionStorage — HomeRedirect
  // also checks it as a safety net in case Better Auth does a full-page redirect).
  const returnUrl = sessionStorage.getItem('bikendure_return_url') ?? '/';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await authClient.signUp.email({
        name,
        email,
        password,
        callbackURL: returnUrl,
      });
      if (result.error) {
        setError(result.error.message ?? 'Sign-up failed. Please try again.');
      } else {
        sessionStorage.removeItem('bikendure_return_url');
        captureEvent('user_signed_up', { method: 'email' });
        navigate(returnUrl, { replace: true });
      }
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card">
        <div className="auth-logo">
          <img src="/logo.png" alt="Bikendure" className="auth-logo-img" />
          <span className="auth-logo-name">Bikendure</span>
        </div>

        <h1 className="auth-title">Create your account</h1>
        <p className="auth-subtitle">Start your AI bike fitting journey</p>

        <GoogleAuthButton mode="sign-up" />

        <div className="auth-divider">
          <span className="auth-divider-text">or use email</span>
        </div>

        <form className="auth-form" onSubmit={handleSubmit} noValidate>
          <div className="auth-field">
            <label htmlFor="name" className="auth-label">Full name</label>
            <input
              id="name"
              type="text"
              className="auth-input"
              value={name}
              onChange={e => setName(e.target.value)}
              required
              autoComplete="name"
              placeholder="Jane Doe"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="email" className="auth-label">Email</label>
            <input
              id="email"
              type="email"
              className="auth-input"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              autoComplete="email"
              placeholder="you@example.com"
            />
          </div>

          <div className="auth-field">
            <label htmlFor="password" className="auth-label">Password</label>
            <input
              id="password"
              type="password"
              className="auth-input"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              placeholder="Min. 8 characters"
              minLength={8}
            />
          </div>

          {error && <p className="auth-error">{error}</p>}

          <button type="submit" className="auth-submit auth-submit--secondary" disabled={loading}>
            {loading ? 'Creating account…' : 'Create account'}
          </button>
        </form>

        <p className="auth-footer-text">
          Already have an account?{' '}
          <Link to="/sign-in" className="auth-link">Sign in</Link>
        </p>
      </div>
    </div>
  );
};

export default SignUpPage;
